import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import { Attachment } from '@panaya/ai-chat/shared';
import { AiChatUiEventsService } from '../ai-chat-ui-events.service';
import { injectAttachmentSources } from './attachment-source.provider';
import { AttachmentChip } from './model/attachment-chip.model';
import { PickerAttachmentSource } from './model/attachment-source.model';
import { ResourceRequestOptions } from './model/resource-request-options.model';
import { resolveAllowedSources } from './resolve-allowed-sources';

/**
 * The attachment chips above the prompt input, and the agent's last `REQUEST_RESOURCES` options, which decide which
 * "+" menu items show. The host owns each item and what it does; this service owns only the chips.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentChipsService {
    private readonly registered = injectAttachmentSources();
    private readonly uiEvents = inject(AiChatUiEventsService);
    private readonly _chips = signal<AttachmentChip[]>([]);
    /** `undefined` while the agent has not asked for resources this turn. */
    private readonly _request = signal<ResourceRequestOptions | undefined>(undefined);
    private nextId = 1;

    public readonly chips = this._chips.asReadonly();
    public readonly resourceRequestOptions = this._request.asReadonly();
    /** The "+" menu items for this turn; empty when the agent asked for nothing or for nothing the host registered. */
    public readonly allowedSources = computed(() => {
        const request = this._request();

        return request === undefined ? [] : resolveAllowedSources(this.registered, request);
    });
    /** True when a chip would go out with the next prompt. File chips never do. */
    public readonly hasSendableChips = computed(() => this._chips().some((chip) => chip.kind === 'entity'));

    constructor() {
        // A new, restored or closed thread starts with an empty prompt bar and no request.
        merge(this.uiEvents.initThreadRequest, this.uiEvents.restoreThreadRequest, this.uiEvents.closeThreadRequest)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.reset());
    }

    /** `undefined` clears the request: the agent did not ask for resources on this turn. */
    public setResourceRequestOptions(options: ResourceRequestOptions | undefined): void {
        this._request.set(options);
    }

    public addPicked(source: PickerAttachmentSource, attachments: Attachment[]): void {
        const added = attachments.map(
            (attachment): AttachmentChip => ({
                id: this.nextId++,
                kind: 'entity',
                label: attachment.label,
                detail: attachment.detail,
                icon: source.icon,
                attachment
            })
        );

        this._chips.update((chips) => [...chips, ...added]);
    }

    public addFiles(files: File[]): void {
        const added = files.map((file): AttachmentChip => ({ id: this.nextId++, kind: 'file', label: file.name, icon: 'file-text', file }));

        this._chips.update((chips) => [...chips, ...added]);
    }

    public remove(id: number): void {
        this._chips.update((chips) => chips.filter((chip) => chip.id !== id));
    }

    /** On send: returns the attachments that go out with the prompt and clears every chip, file chips included. */
    public takeSendableAttachments(): Attachment[] {
        const attachments = this._chips().flatMap((chip) => (chip.kind === 'entity' ? [chip.attachment] : []));

        this._chips.set([]);

        return attachments;
    }

    public reset(): void {
        this._chips.set([]);

        this._request.set(undefined);
    }
}
